import {
  IonButtons,
  IonContent,
  IonFooter,
  IonLabel,
  IonPage,
  IonToolbar,
  useIonToast,
} from '@ionic/react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SubscribeToPlanLineItemInput,
  UpdatePlanSubscriptionLineItemInput,
  useCreateLinkToManagePlanSubscriptionMutation,
  useCreateLinkToSubscribeToPlanMutation,
  useUpdatePlanSubscriptionMutation,
} from '../../api/graphql/generated';
import { useConfirmActionAlert } from '../../components/alert/confirmation-alert';
import { LoadingButton } from '../../components/button/loading-button';
import { PageHeader } from '../../components/header/page.header';
import { Subscription } from '../../components/subscription/subscription';
import {
  SubscriptionCart,
  SubscriptionContextProvider,
  useSubscriptionContext,
} from '../../components/subscription/subscription.context';
import './subscription.page.css';
import { i18nKeyType } from '../../i18n';
import { getLocalizedText } from '../../i18n/i18n.utils';
import { openLink } from '../../util/link-utils';
import { logger } from '../../util/logger';

const SubscriptionPage = () => {
  return (
    <SubscriptionContextProvider>
      <SubscriptionPageInner />
    </SubscriptionContextProvider>
  );
};

const SubscriptionPageInner = () => {
  const [showToast] = useIonToast();
  const { i18n } = useTranslation();

  const { cart, hasExistingSubscription, hasPendingChangesInCart } =
    useSubscriptionContext();

  const confirmActionAlert = useConfirmActionAlert();

  const [
    createLinkToSubscribeToPlan,
    { loading: createLinkToSubscribeToPlanLoading },
  ] = useCreateLinkToSubscribeToPlanMutation();

  const [
    createLinkToManagePlanSubscription,
    { loading: createLinkToManagePlanLoading },
  ] = useCreateLinkToManagePlanSubscriptionMutation();

  const [updatePlanSubscription, { loading: updatePlanSubscriptionLoading }] =
    useUpdatePlanSubscriptionMutation();

  const subscriptionActionLabel = useMemo(() => {
    const key: i18nKeyType = 'common:checkout';

    if (hasExistingSubscription) {
      return i18n.t('common:update');
    }

    return i18n.t(key);
  }, [i18n, hasExistingSubscription]);

  const cartTotalLabel = useMemo(() => {
    const total = i18n.t('common:format_currency', {
      value: {
        amount: cart.total,
        currency: cart.currency,
      },
    });

    const interval = (
      i18n.t(`common:billing_interval_${cart.interval}`) as string
    ).toLowerCase();

    return `${total} / ${interval}`;
  }, [cart, i18n]);

  const openManageSubscription = useCallback(async () => {
    if (!hasExistingSubscription) {
      return;
    }

    const { data } = await createLinkToManagePlanSubscription();
    const url = data?.createLinkToManagePlanSubscription;

    if (url) {
      openLink(url);
    }
  }, [hasExistingSubscription, createLinkToManagePlanSubscription]);

  const onSubscriptionActionClick = useCallback(
    async (cart: SubscriptionCart) => {
      if (hasExistingSubscription) {
        const items: UpdatePlanSubscriptionLineItemInput[] = cart.items.map(
          (item) => {
            return {
              planId: item.plan.id,
              priceId: item.price.id,
              quantity: item.quantity,
              assignedToIds: item.assignedToIds,
            };
          }
        );

        // Stripe requires at least one item in a subscription.
        // If the user has removed everything then rather than update, they should cancel.
        if (items.length === 0) {
          const showCancelPrompt = confirmActionAlert({
            header: getLocalizedText(
              'common:cancel_subscription_prompt_header'
            ),
            message: i18n.t('common:cancel_subscription_prompt_message'),
            cancelText: i18n.t('no'),
            confirmText: i18n.t('yes'),
            isDestructive: true,
            confirmationAction: async () => {
              await openManageSubscription();
            },
          });
          showCancelPrompt();
          return;
        }

        // Confirm new amount then update subscription.
        const showUpdatePrompt = confirmActionAlert({
          header: getLocalizedText('common:update_subscription_prompt_header'),
          message: i18n.t('common:update_subscription_prompt_message', {
            newTotal: cartTotalLabel,
          }),
          confirmationAction: async () => {
            const { data, errors } = await updatePlanSubscription({
              variables: {
                input: {
                  items,
                },
              },
            });
            if (errors || data?.updatePlanSubscription !== 'true') {
              showToast({
                message: i18n.t('update_subscription_toast_error_message'),
                duration: 3500,
                color: 'danger',
                position: 'top',
              });
              logger.error('failed to update subscription', {
                cart,
                errors,
              });
            } else {
              showToast({
                message: i18n.t('update_subscription_toast_success_message'),
                duration: 3500,
                color: 'success',
                position: 'top',
              });
            }
          },
        });
        showUpdatePrompt();
      } else {
        const items: SubscribeToPlanLineItemInput[] = cart.items.map((item) => {
          return {
            priceId: item.price.id,
            assignedToIds: item.assignedToIds,
          };
        });

        const { data } = await createLinkToSubscribeToPlan({
          variables: {
            input: {
              items,
            },
          },
        });

        const url = data?.createLinkToSubscribeToPlan;

        if (url) {
          openLink(url);
        }
      }
    },
    [
      i18n,
      cartTotalLabel,
      hasExistingSubscription,
      showToast,
      openManageSubscription,
      confirmActionAlert,
      updatePlanSubscription,
      createLinkToSubscribeToPlan,
    ]
  );

  const actionLoading =
    createLinkToSubscribeToPlanLoading || updatePlanSubscriptionLoading;

  return (
    <IonPage>
      <PageHeader
        title={i18n.t('common:page_subscription')}
        rightButtons={
          hasExistingSubscription ? (
            <LoadingButton
              loading={createLinkToManagePlanLoading}
              buttonProps={{ onClick: openManageSubscription }}
            >
              {i18n.t('common:manage_your_plan')}
            </LoadingButton>
          ) : undefined
        }
      />
      <IonContent>
        <Subscription />
      </IonContent>
      <IonFooter className="subscription-page-footer">
        <IonToolbar>
          <IonLabel className="subscription-cart-total" slot="start">
            {cartTotalLabel}
          </IonLabel>
          <IonButtons slot="end">
            {hasPendingChangesInCart && (
              <LoadingButton
                loading={actionLoading}
                buttonProps={{
                  disabled: !hasPendingChangesInCart,
                  onClick: () => onSubscriptionActionClick(cart),
                }}
              >
                {subscriptionActionLabel}
              </LoadingButton>
            )}
          </IonButtons>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export { SubscriptionPage };
